import * as stylex from '@stylexjs/stylex'

import { breakpoints, colors, fonts, fontSizes, motion, radii, sizes, spacing } from '../tokens/tokens.stylex'

export const styles = stylex.create({
  page: {
    width: '100%',
    flex: 1,
    borderBlockEndWidth: 1,
    borderBlockEndStyle: 'solid',
    borderBlockEndColor: colors.marketingBorder,
    backgroundColor: colors.marketingBackground,
  },
  frame: {
    boxSizing: 'border-box',
    marginInline: 'auto',
    display: 'flex',
    maxWidth: sizes.editorial,
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingInline: { default: spacing.space32, [breakpoints.medium]: spacing.space48 },
    paddingBlockStart: { default: spacing.space48, [breakpoints.wide]: spacing.space64 },
    paddingBlockEnd: { default: 72, [breakpoints.wide]: 96 },
  },
  contactFrame: { gap: spacing.space32 },
  header: { display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'flex-start', gap: spacing.space14 },
  eyebrow: {
    fontFamily: fonts.marketingMono,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.space8,
    fontSize: fontSizes.label,
    lineHeight: 'normal',
    letterSpacing: '0.28px',
    color: colors.marketingEyebrow,
    margin: 0,
  },
  title: {
    fontFamily: fonts.marketingMono,
    fontSize: { default: fontSizes.pageTitle, [breakpoints.medium]: fontSizes.pageTitleWide },
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: '-1px',
    color: colors.marketingForeground,
    margin: 0,
  },
  contactTitle: { lineHeight: 1.11 },
  subtitle: {
    fontFamily: fonts.marketingSans,
    fontSize: { default: fontSizes.dialogTitle, [breakpoints.medium]: fontSizes.title },
    lineHeight: 1.45,
    color: colors.marketingEyebrow,
    margin: 0,
  },
  byline: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.small,
    lineHeight: 'normal',
    letterSpacing: '0.39px',
    color: colors.marketingQuiet,
    margin: 0,
  },
  content: { marginBlockStart: spacing.space28, width: '100%' },
  heading: { fontFamily: fonts.marketingMono, color: colors.marketingForeground, margin: 0 },
  section: { fontSize: fontSizes.dialogTitle, lineHeight: 1.3, fontWeight: 600, letterSpacing: '-0.4px' },
  subsection: { fontSize: fontSizes.editorialBody, lineHeight: 1.35, fontWeight: 500, letterSpacing: '-0.17px' },
  compact: { fontSize: fontSizes.reading, lineHeight: 1.4, fontWeight: 600, letterSpacing: '-0.1px' },
  lead: { fontSize: fontSizes.title, lineHeight: 1.25, fontWeight: 600, letterSpacing: '-0.44px' },
  sectionSpacing: { marginBlockStart: 35, marginBlockEnd: spacing.space14 },
  subsectionSpacing: { marginBlockStart: 26, marginBlockEnd: spacing.space10 },
  compactSpacing: { marginBlockStart: 22, marginBlockEnd: spacing.space6 },
  directorySpacing: { marginBlockStart: spacing.space40, marginBlockEnd: 18 },
  leadSpacing: { marginBlockStart: spacing.space8, marginBlockEnd: spacing.space6 },
  mediaSpacing: { marginBlockEnd: spacing.space6 },
  anchored: { scrollMarginBlockStart: 88 },
  roleAnchor: { scrollMarginBlockStart: spacing.space32 },
  text: { fontFamily: fonts.marketingSans, color: colors.editorialBody, margin: 0 },
  body: { fontSize: fontSizes.editorialBody, lineHeight: 1.65, marginBlockEnd: '0.5lh' },
  supporting: { fontSize: fontSizes.body, lineHeight: 1.6 },
  description: { fontSize: fontSizes.sectionTitle, lineHeight: 1.6, color: colors.marketingSecondary },
  event: { fontSize: fontSizes.body, lineHeight: 1.5 },
  metadata: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.small,
    lineHeight: 'normal',
    letterSpacing: '0.39px',
    color: colors.marketingQuiet,
  },
  fine: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.small,
    lineHeight: 1.6,
    letterSpacing: '0.39px',
    color: colors.marketingQuiet,
  },
  link: {
    color: colors.marketingForeground,
    textDecoration: 'underline',
    textDecorationColor: colors.editorialLink,
    textUnderlineOffset: 3,
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.space8,
    color: { default: colors.marketingEyebrow, ':hover': colors.marketingForeground },
    textDecoration: 'none',
    transitionProperty: 'color',
    transitionDuration: motion.editorialDuration,
  },
  list: {
    fontFamily: fonts.marketingSans,
    margin: 0,
    marginBlockEnd: '0.5lh',
    paddingInlineStart: 22,
    fontSize: fontSizes.editorialBody,
    lineHeight: 1.65,
    color: colors.editorialBody,
  },
  unordered: { listStyleType: 'disc' },
  ordered: { listStyleType: 'decimal' },
  divider: {
    borderWidth: 0,
    borderBlockStartWidth: 1,
    borderBlockStartStyle: 'solid',
    borderBlockStartColor: colors.marketingBorder,
    width: '100%',
    marginInline: 0,
    marginBlock: spacing.space40,
  },
  headerDivider: {
    marginBlockStart: spacing.space28,
    marginBlockEnd: 0,
    borderBlockStartColor: colors.editorialHairline,
  },
  directoryDivider: { marginBlock: spacing.space32 },
  chapterDivider: { marginBlock: 80 },
  image: {
    marginBlock: spacing.space10,
    display: 'block',
    width: '100%',
    borderRadius: radii.medium,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.marketingCardBorder,
  },
  card: {
    boxSizing: 'border-box',
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    gap: spacing.space14,
    borderRadius: radii.medium,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.marketingCardBorder,
    transitionProperty: 'background-color',
    transitionDuration: motion.editorialDuration,
    backgroundColor: {
      default: 'transparent',
      ':hover': colors.marketingCardHover,
      ':active': colors.marketingCardActive,
    },
    textDecoration: 'none',
  },
  articleCard: { overflow: 'clip', paddingInline: 22, paddingBlock: spacing.space24 },
  contactCard: { paddingInline: 18, paddingBlock: spacing.space16 },
  cardContent: {
    display: 'flex',
    minWidth: 1,
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing.space14,
  },
  contactContent: { gap: spacing.space6, lineHeight: 'normal' },
  cardHeadingGroup: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing.space6,
  },
  cardTitle: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.editorialCard,
    lineHeight: 'normal',
    fontWeight: 500,
    letterSpacing: '-0.19px',
    color: colors.marketingForeground,
    margin: 0,
  },
  contactCardTitle: { fontSize: fontSizes.body, letterSpacing: '-0.16px' },
  cardSubtitle: {
    fontFamily: fonts.marketingSans,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: fontSizes.body,
    lineHeight: 'normal',
    letterSpacing: '0.48px',
    color: colors.editorialSubtitle,
    margin: 0,
  },
  cardMeta: {
    fontFamily: fonts.marketingMono,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: fontSizes.label,
    lineHeight: 'normal',
    fontWeight: 400,
    letterSpacing: '0.42px',
    color: colors.marketingQuiet,
    margin: 0,
  },
  contactDetail: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.small,
    letterSpacing: '0.39px',
    color: colors.marketingQuiet,
  },
  cardTrailing: { flexShrink: 0, paddingBlockStart: spacing.space6 },
  contactTrailing: { paddingBlockStart: spacing.space4 },
})
